import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaTrash, FaEye, FaSearch, FaClipboardList, FaMapMarkerAlt, FaShareAlt } from 'react-icons/fa';
import '../pages_premium.css';
import { getFrontendBaseUrl } from '../utils/url';
import AdminShareModal from '../components/common/AdminShareModal';
import AdminPagination from '../components/common/AdminPagination';

function HiremateTasksList() {
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [shareItem, setShareItem] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const handleShareTask = (task) => {
        const frontendUrl = getFrontendBaseUrl();
        setShareItem({
            url: `${frontendUrl}/hiremate/${task.id}`,
            title: task.task_title || task.title || 'User Task',
            category: 'Hiremate Task',
            price: task.budget ? `$${task.budget}` : '',
            location: task.location || '',
            description: task.description || ''
        });
    };

    const fetchTasks = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/admin/hiremate-tasks');
            setTasks(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error('Error fetching hiremate tasks:', error);
            toast.error('Failed to fetch tasks');
            setTasks([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTasks();
    }, []);

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this task listing?")) {
            try {
                await axiosInstance.delete(`/admin/hiremate-tasks/${id}`);
                toast.success('Task listing deleted successfully');
                fetchTasks();
            } catch (error) {
                console.error('Error deleting task:', error);
                toast.error('Failed to delete task');
            }
        }
    };

    const parseJSON = (val, fallback = []) => {
        if (!val) return fallback;
        if (typeof val === 'object') return val;
        try {
            return JSON.parse(val);
        } catch (e) {
            return fallback;
        }
    };

    const formatDateTimeSingleLine = (task) => {
        let dateStr = '';
        if (task.date_type === 'flexible') {
            dateStr = 'Flexible Date';
        } else if (task.task_date) {
            dateStr = task.task_date;
            if (task.date_type === 'before_date') {
                dateStr = `Before ${dateStr}`;
            }
        } else {
            dateStr = 'Flexible Date';
        }

        let timeStr = '';
        if (task.time_of_day) {
            const times = parseJSON(task.time_of_day, task.time_of_day);
            timeStr = Array.isArray(times) ? times.join(', ') : String(times);
        }

        if (dateStr && timeStr) {
            return `${dateStr} • ${timeStr}`;
        }
        return dateStr || timeStr || 'Flexible';
    };

    const filtered = tasks.filter(task => {
        const title = task.title || '';
        const posterName = task.Poster?.name || '';
        const suburb = task.suburb || '';
        const query = search.toLowerCase();
        return title.toLowerCase().includes(query) ||
            posterName.toLowerCase().includes(query) ||
            suburb.toLowerCase().includes(query);
    });

    const paginatedTasks = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return (
        <div className="page-premium-wrapper hiremate-tasks-list-page">
            <div className="page-premium-content">

                {/* Hero Header */}
                <div className="page-hero-header page-hero-properties">
                    <div className="ph-blob ph-blob-1"></div>
                    <div className="ph-blob ph-blob-2"></div>

                    <div className="ph-left">
                        <div className="ph-icon">
                            <FaClipboardList size={26} />
                        </div>
                        <div>
                            <h1 className="ph-title">User Task List</h1>
                            <p className="ph-subtitle">Monitor and manage all personal tasks listed by users (e.g. cleaning, moving, repairs)</p>
                        </div>
                    </div>

                    <div className="ph-right">
                        <div className="ph-count-badge">
                            <span className="count-num">{tasks.length}</span>
                            <span className="count-lbl">Total Tasks</span>
                        </div>
                    </div>
                </div>

                {/* Table Card */}
                <div className="premium-table-card">
                    <div className="ptable-toolbar">
                        <div className="ptable-title">
                            <span className="ptable-title-dot"></span>
                            Listed Tasks
                        </div>
                        <div className="ptable-search">
                            <FaSearch className="ptable-search-icon" />
                            <input
                                type="text"
                                placeholder="Search by task title, poster or location..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="ptable-wrap">
                        {loading ? (
                            <div className="text-center py-5 text-secondary">Loading tasks...</div>
                        ) : (
                            <table className="premium-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '50px' }}>#</th>
                                        <th style={{ minWidth: '220px' }}>Task Title</th>
                                        <th style={{ minWidth: '180px' }}>Posted By</th>
                                        <th style={{ minWidth: '130px' }}>Budget / Pay</th>
                                        <th style={{ minWidth: '220px', whiteSpace: 'nowrap' }}>Scheduled Date & Time</th>
                                        <th style={{ minWidth: '100px' }}>Status</th>
                                        <th style={{ textAlign: 'center', width: '130px' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedTasks.map((task, index) => {
                                        const poster = task.Poster || {};
                                        const isHourly = task.budget_type === 'hourly';

                                        return (
                                            <tr key={task.id}>
                                                <td style={{ color: '#9ba6c0', fontWeight: 600 }}>#{(currentPage - 1) * pageSize + index + 1}</td>
                                                <td>
                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                        <strong style={{ color: '#0f172a', fontSize: '0.9rem' }}>{task.title}</strong>
                                                        <span style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                                                            <FaMapMarkerAlt size={10} style={{ color: '#0ab68e' }} />
                                                            {task.location_type === 'online' ? 'Remote / Online' : (task.suburb || 'In Person')}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                        <strong style={{ color: '#0f172a', fontSize: '0.88rem', whiteSpace: 'nowrap' }}>{poster.name || 'Unknown User'}</strong>
                                                        <span style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>{poster.email || '-'}</span>
                                                        <span style={{ fontSize: '11px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{poster.phone || '-'}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span style={{
                                                        backgroundColor: '#f1f5f9',
                                                        color: '#0f172a',
                                                        padding: '4px 10px',
                                                        borderRadius: '12px',
                                                        fontWeight: '700',
                                                        fontSize: '12px',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        ${parseFloat(task.budget || 0).toFixed(2)} {isHourly ? '/ hr' : '(Total)'}
                                                    </span>
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    <span style={{ fontSize: '13px', color: '#334155', fontWeight: '500', whiteSpace: 'nowrap' }}>
                                                        {formatDateTimeSingleLine(task)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`ptag ${task.status === 'open' ? 'ptag-active' : 'ptag-banned'}`} style={{ textTransform: 'capitalize' }}>
                                                        {task.status || 'open'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="hire-actions" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                        <button
                                                            onClick={() => handleShareTask(task)}
                                                            className="action-btn action-btn-share"
                                                            data-tooltip="Share Task"
                                                            title="Share Task"
                                                            style={{ backgroundColor: '#ef4444', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer' }}
                                                        >
                                                            <FaShareAlt />
                                                        </button>
                                                        <button
                                                            onClick={() => navigate(`/hiremate-tasks/${task.id}`)}
                                                            className="action-btn action-btn-view"
                                                            data-tooltip="View Task Details"
                                                            style={{ backgroundColor: '#0ab68e', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer' }}
                                                        >
                                                            <FaEye />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(task.id)}
                                                            className="action-btn action-btn-delete"
                                                            data-tooltip="Delete Task"
                                                            style={{ backgroundColor: '#ef4444', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer' }}
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {filtered.length === 0 && (
                                        <tr>
                                            <td colSpan="7">
                                                <div className="ptable-empty">
                                                    <div className="ptable-empty-icon">📝</div>
                                                    <p>No user tasks found.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <AdminPagination
                        currentPage={currentPage}
                        totalItems={filtered.length}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={setPageSize}
                        itemLabel="tasks"
                    />
                </div>

            </div>

            <AdminShareModal
                isOpen={!!shareItem}
                onClose={() => setShareItem(null)}
                {...shareItem}
            />
        </div>
    );
}

export default HiremateTasksList;
